import { motion, type Variants } from "framer-motion";
import type { CSSProperties, ElementType, ReactNode } from "react";

import { useReducedMotionSafe } from "@/hooks/useReducedMotionSafe";
import { fadeSide, fadeUp, staticVariants, viewportOnce } from "@/lib/motion";

type RevealDirection = "up" | "left" | "right";

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Eintrittsrichtung. Standard: von unten. */
  direction?: RevealDirection;
  /** Zusätzliche Verzögerung in Sekunden. */
  delay?: number;
  /** Gerendertes Element (z. B. "section", "li"). Standard: div. */
  as?: ElementType;
  /** Eigene Varianten, falls der Baustein in einer Stagger-Gruppe sitzt. */
  variants?: Variants;
  /** Inline-Styles (z. B. Akzent-Variablen), werden 1:1 durchgereicht. */
  style?: CSSProperties;
}

/**
 * REGEL: Standard-Eintritt für Inhalte beim Scrollen.
 * Feuert genau einmal (viewportOnce) und fällt bei "Bewegung reduzieren"
 * vollständig auf statische Darstellung zurück.
 */
const Reveal = ({
  children,
  className,
  direction = "up",
  delay = 0,
  as = "div",
  variants,
  style,
}: RevealProps) => {
  const reduced = useReducedMotionSafe();
  const MotionTag = motion(as as ElementType);

  const resolved: Variants = reduced
    ? staticVariants
    : variants ?? (direction === "up" ? fadeUp : fadeSide(direction));

  return (
    <MotionTag
      className={className}
      style={style}
      variants={resolved}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      transition={reduced ? { duration: 0 } : { delay }}
    >
      {children}
    </MotionTag>
  );
};

export default Reveal;
